import React, { useState, useEffect } from 'react';
import { Calculator, PieChart, Percent, Plus, Minus } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const MarketConcentrationCalculator = () => {
  const [companies, setCompanies] = useState([
    { id: 1, marketShare: 30 },
    { id: 2, marketShare: 25 },
    { id: 3, marketShare: 15 },
    { id: 4, marketShare: 10 },
  ]);
  const [hhi, setHhi] = useState(0);
  const [concentrationType, setConcentrationType] = useState('');

  useEffect(() => {
    calculateHHI();
  }, [companies]);

  const calculateHHI = () => {
    const calculatedHhi = companies.reduce((sum, company) => {
      return sum + Math.pow(company.marketShare, 2);
    }, 0);
    setHhi(parseFloat(calculatedHhi.toFixed(2)));

    // Determine market concentration type
    if (calculatedHhi < 1500) {
      setConcentrationType('Unconcentrated Market');
    } else if (calculatedHhi >= 1500 && calculatedHhi < 2500) {
      setConcentrationType('Moderately Concentrated Market');
    } else {
      setConcentrationType('Highly Concentrated Market');
    }
  };

  const addCompany = () => {
    const newId = companies.length > 0 ? Math.max(...companies.map(c => c.id)) + 1 : 1;
    setCompanies([...companies, { id: newId, marketShare: 0 }]);
  };

  const removeCompany = (id: number) => {
    if (companies.length > 1) {
      setCompanies(companies.filter(company => company.id !== id));
    }
  };

  const updateMarketShare = (id: number, value: number) => {
    setCompanies(companies.map(company => 
      company.id === id ? { ...company, marketShare: value } : company
    ));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/market-concentration-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Market Concentration Calculator: Measure Industry Competition | Free Tool</title>
        <meta name="description" content="Calculate market concentration using HHI (Herfindahl-Hirschman Index). Analyze competitive landscape, assess market structure, and evaluate merger impacts." />
        <meta name="keywords" content="market concentration calculator, HHI calculator, competitive analysis, market structure, merger assessment, industry competition" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Market Concentration Calculator: Analyze Industry Competition" />
        <meta property="og:description" content="Calculate market concentration using HHI. Get insights on competitive landscape and market structure analysis." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Market Concentration Calculator: Analyze Industry Competition" />
        <meta property="twitter:description" content="Calculate market concentration using HHI. Get insights on competitive landscape and market structure analysis." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Market Concentration Calculator",
            "description": "Free calculator to measure market concentration and analyze industry competition",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate HHI",
              "Industry Benchmarks",
              "Market Analysis",
              "Competition Assessment",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Market Concentration",
              "description": "A measure of market competitiveness using the Herfindahl-Hirschman Index (HHI)."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Market Analysts, Competition Authorities, Business Strategists"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Market Concentration",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter market shares for each company"
                },
                {
                  "@type": "HowToStep",
                  "text": "Add or remove companies as needed"
                },
                {
                  "@type": "HowToStep",
                  "text": "View HHI score and market concentration type"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Market Concentration Calculator (HHI)
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Market Shares</h2>
          <div className="space-y-4">
            {companies.map((company) => (
              <div key={company.id} className="flex items-center space-x-4">
                <div className="flex-grow">
                  <label className="block text-sm font-medium text-gray-700">
                    Company {company.id} Market Share (%)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Percent className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      value={company.marketShare}
                      onChange={(e) => updateMarketShare(company.id, Number(e.target.value))}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                      placeholder="0"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeCompany(company.id)}
                  className="mt-6 p-2 text-red-600 hover:text-red-800"
                >
                  <Minus className="h-5 w-5" />
                </button>
              </div>
            ))}
            <button
              onClick={addCompany}
              className="mt-4 flex items-center text-indigo-600 hover:text-indigo-800"
            >
              <Plus className="h-5 w-5 mr-1" />
              Add Company
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {hhi}
            <PieChart className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Herfindahl-Hirschman Index (HHI)</p>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-lg font-semibold text-gray-800">{concentrationType}</p>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Guidelines</h2>
        <p className="text-gray-600 mb-4">
          The Herfindahl-Hirschman Index (HHI) is {hhi}, indicating a {concentrationType.toLowerCase()}. 
          This measurement helps assess market competitiveness and evaluate potential merger impacts.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">HHI Thresholds</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Less than 1,500: Unconcentrated Market</li>
          <li>1,500 to 2,500: Moderately Concentrated Market</li>
          <li>More than 2,500: Highly Concentrated Market</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Market Structure Types</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Perfect Competition: HHI approaches 0</li>
          <li>Monopolistic Competition: HHI less than 1,500</li>
          <li>Oligopoly: HHI between 1,500 and 2,500</li>
          <li>Dominant Firm: HHI more than 2,500</li>
          <li>Monopoly: HHI approaches 10,000</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Regulatory Implications</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Merger Guidelines: HHI increase of more than 100 points in concentrated markets may raise concerns</li>
          <li>Antitrust Review: Higher HHI may trigger closer regulatory scrutiny</li>
          <li>Market Power: Higher concentration suggests greater market power</li>
          <li>Competition Policy: Used to assess need for market intervention</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Competitive Dynamics: Assess market structure and competition level</li>
          <li>Entry Barriers: Higher HHI often indicates significant entry barriers</li>
          <li>Market Efficiency: Lower HHI suggests more competitive and efficient markets</li>
          <li>Strategic Planning: Use HHI to inform competitive strategy</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Market Entry: Decrease of 500 to 1000 HHI points</li>
          <li>Major Merger: Increase of 150 to 400 HHI points</li>
          <li>Market Exit: Increase of 200 to 500 HHI points</li>
          <li>Industry Consolidation: Increase of 300 to 800 HHI points</li>
        </ul>
      </div>
    </div>
  );
};

export default MarketConcentrationCalculator;
